import { useTranslation } from 'react-i18next';
import { effectieveDrempel } from '../drempel';
import { formatDatum, formatGetal, isTelling } from '../format';
import { isVoorWeekrapport } from '../measurement-version';
import { heeftEigenNaam, veldnaamVan } from '../veldnaam';
import { weeknummer } from '../week';
import { AccountabilityFooter } from './AccountabilityFooter';
import { Aandachtskaart } from './Aandachtskaart';
import { Doelbalk } from './Doelbalk';
import { MeerOverDezeWeek, type MeerOverWerksoort, type OverigVeld } from './MeerOverDezeWeek';
import { Richtingstekst } from './Richtingstekst';
import type {
  EigenGemetenWerksoort,
  EigenHoofdveld,
  EigenPersoonsweek,
  OngemetenWerksoort,
  Veldnamen,
  Weekcijfer,
  WerksoortSleutel,
} from '../work-quality.api';

/** Vastleggen, dan Controles; nooit samen in één cijfer of lijst (M4). */
const WERKSOORTEN: ReadonlyArray<{ sleutel: WerksoortSleutel; naam: string }> = [
  { sleutel: 'myxxtract', naam: 'qa.eigen.werksoortMyxxtract' },
  { sleutel: 'pca', naam: 'qa.eigen.werksoortPca' },
];

/** Het aantal Aandachtskaarten per werksoort (M10). */
const TOP = 3;

type Cijfer = Extract<Weekcijfer, { percentage: number }>;

/**
 * Wat er van één werksoort op het scherm komt, gelezen uit het antwoord; hier wordt niets uitgerekend.
 * `R` is het eigen rapport (Mijn feedback) of het volle rapport van een collega (Details, story 19).
 */
export type Stand<R extends EigenGemetenWerksoort = EigenGemetenWerksoort> =
  | { soort: 'cijfer'; rapport: R; cijfer: Cijfer }
  | { soort: 'teWeinig'; aantal?: number }
  | { soort: 'geenWerk' | 'nietNagekeken' | 'oud' | 'mislukt' | 'nietBerekend' };

function isCijfer(waarde: Weekcijfer | undefined): waarde is Cijfer {
  return (
    typeof waarde === 'object' &&
    waarde !== null &&
    waarde.reden === undefined &&
    isTelling(waarde.percentage) &&
    isTelling(waarde.aantalInEenKeerGoed) &&
    isTelling(waarde.aantalBeoordeeld)
  );
}

export function standVan<R extends EigenGemetenWerksoort = EigenGemetenWerksoort>(rapport: R | OngemetenWerksoort | undefined): Stand<R> {
  if (typeof rapport !== 'object' || rapport === null) return { soort: 'nietBerekend' };
  if (rapport.reden === 'geen_beoordeeld_werk') return { soort: 'nietNagekeken' };
  if (rapport.reden === 'niet_gemeten_in_deze_meetversie') return { soort: 'oud' };
  if (rapport.reden !== undefined) return { soort: 'nietBerekend' };
  const cijfer = rapport.inEenKeerGoed;
  if (isCijfer(cijfer)) return { soort: 'cijfer', rapport: rapport as R, cijfer };
  switch (cijfer?.reden) {
    case 'te_weinig_gegevens':
      return { soort: 'teWeinig', aantal: isTelling(cijfer.aantalBeoordeeld) ? cijfer.aantalBeoordeeld : undefined };
    case 'geen_werk_in_werksoort':
      return { soort: 'geenWerk' };
    case 'niet_nagekeken':
      return { soort: 'nietNagekeken' };
    case 'niet_gemeten_in_deze_meetversie':
      return { soort: 'oud' };
    case 'meting_mislukt':
      return { soort: 'mislukt' };
    default:
      // Ontbrekend, `nog_niet_gemeten` of een vorm die het portaal niet kent: nooit een lege werksoort.
      return { soort: 'nietBerekend' };
  }
}

/** Een veld met een aantal, zonder reden: kandidaat voor een kaart of voor "Meer over deze week". */
export function metAantal(veld: unknown): veld is EigenHoofdveld {
  if (typeof veld !== 'object' || veld === null) return false;
  const v = veld as Partial<EigenHoofdveld> & { reden?: unknown };
  return v.reden === undefined && typeof v.veld === 'string' && isTelling(v.aantalProductenMetCorrectie) && v.aantalProductenMetCorrectie >= 1;
}

/**
 * De velden van één werksoort (nooit `Persoonsweek.velden`, die telt beide samen; M4): gesorteerd op aantal,
 * gelijke aantallen in de volgorde van de dienst (een stabiele sortering). Velden in bespreking staan nergens
 * (M11); te weinig gegevens alleen met naam, achteraan.
 */
function veldenVan(rapport: EigenGemetenWerksoort, veldnamen: Veldnamen | undefined, onbekend: string) {
  const velden: unknown[] = Array.isArray(rapport.hoofdsamenvatting?.velden) ? rapport.hoofdsamenvatting.velden : [];
  const naam = (pad: string) => (heeftEigenNaam(pad, veldnamen) ? veldnaamVan(pad, veldnamen) : onbekend);
  const gesorteerd = velden.filter(metAantal).sort((a, b) => b.aantalProductenMetCorrectie - a.aantalProductenMetCorrectie);
  const teWeinig = velden.filter(
    (v): v is { veld: string } =>
      typeof v === 'object' && v !== null && (v as { reden?: unknown }).reden === 'te_weinig_gegevens' && typeof (v as { veld?: unknown }).veld === 'string',
  );
  const overig: OverigVeld[] = [
    ...gesorteerd.slice(TOP).map((v) => ({ veld: v.veld, naam: naam(v.veld), aantal: v.aantalProductenMetCorrectie })),
    ...teWeinig.map((v) => ({ veld: v.veld, naam: naam(v.veld) })),
  ];
  return { kaarten: gesorteerd.slice(0, TOP), overig };
}

const BLOK_KOP = 'text-[0.9375rem] font-semibold text-[var(--foreground-heading)]';

function Resultaatblok({ sleutel, naam, rapport, cijfer, doel, kopNiveau }: {
  sleutel: WerksoortSleutel;
  naam: string;
  rapport: EigenGemetenWerksoort;
  cijfer: Cijfer;
  doel?: number;
  kopNiveau: 2 | 3 | 4;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const getal = (waarde: number) => formatGetal(waarde, locale);
  const Kop = `h${kopNiveau}` as const;
  const vorige = isCijfer(rapport.vorigeWeek) ? rapport.vorigeWeek : undefined;
  const punten = typeof rapport.verschilPunten === 'number' ? rapport.verschilPunten : null;
  const basis = { week: weeknummer(cijfer.weekKey), percentage: getal(cijfer.percentage) };
  const metVorige = vorige === undefined ? {} : { vorige: getal(vorige.percentage) };

  let zin: string;
  if ((rapport.richting === 'hoger' || rapport.richting === 'lager') && punten !== null) {
    const sleutel = rapport.richting === 'hoger' ? 'qa.rapportkaart.zinHoger' : 'qa.rapportkaart.zinLager';
    zin = t(vorige === undefined ? `${sleutel}ZonderVorige` : sleutel, { ...basis, ...metVorige, count: punten, punten: getal(punten) });
  } else if (rapport.richting === 'gelijk') {
    zin = t(vorige === undefined ? 'qa.rapportkaart.zinGelijkZonderVorige' : 'qa.rapportkaart.zinGelijk', { ...basis, ...metVorige });
  } else {
    zin = t('qa.rapportkaart.zinGeenVergelijking', basis);
  }
  if (doel !== undefined) zin = t('qa.rapportkaart.zinMetDoel', { zin, doel: getal(doel) });

  return (
    <section className="flex min-w-0 flex-col gap-2 rounded-[12px] bg-[var(--background)] p-5 shadow-[var(--card-shadow)]" data-testid={`werkkwaliteit-resultaat-${sleutel}`}>
      <Kop className={BLOK_KOP}>{naam}</Kop>
      <p className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-[2.5rem] font-bold leading-none text-[var(--foreground-heading)] tabular-nums">
          {t('qa.rapportkaart.percentage', { percentage: getal(cijfer.percentage) })}
        </span>
        <span className="text-[0.8125rem] font-medium text-[var(--foreground)]">{t('qa.rapportkaart.inEenKeerGoed')}</span>
      </p>
      <p>
        <Richtingstekst richting={rapport.richting} verschilPunten={rapport.verschilPunten} vorige={rapport.vorigeWeek} />
      </p>
      <p className="text-[0.875rem] text-[var(--foreground)]">{zin}</p>
      <p className="text-[0.75rem] text-[var(--foreground-muted)]">
        {t('qa.rapportkaart.xVanY', { goed: getal(cijfer.aantalInEenKeerGoed), beoordeeld: getal(cijfer.aantalBeoordeeld) })}
      </p>
      <Doelbalk
        doel={doel}
        percentage={cijfer.percentage}
        vorige={vorige === undefined ? undefined : { weekKey: vorige.weekKey, percentage: vorige.percentage }}
      />
    </section>
  );
}

function OnderDeGrensBlok({ sleutel, naam, stand, drempel, kopNiveau }: {
  sleutel: WerksoortSleutel;
  naam: string;
  stand: Exclude<Stand, { soort: 'cijfer' }>;
  drempel: number;
  kopNiveau: 2 | 3 | 4;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const Kop = `h${kopNiveau}` as const;
  const zinnen: string[] = (() => {
    switch (stand.soort) {
      case 'teWeinig':
        return [
          stand.aantal === undefined
            ? t('qa.rapportkaart.teWeinigZonderAantal')
            : t('qa.rapportkaart.teWeinig', { aantal: formatGetal(stand.aantal, locale) }),
          t('qa.rapportkaart.vanafDrempel', { drempel: formatGetal(drempel, locale) }),
        ];
      case 'geenWerk':
        return [t('qa.rapportkaart.geenWerk', { werksoort: naam })];
      case 'nietNagekeken':
        return [t('qa.eigen.werksoortGeenBeoordeeldWerk'), t('qa.eigen.werksoortGeenBeoordeeldWerkUitleg')];
      case 'oud':
        return [t('qa.eigen.weekOudeMeetmethode')];
      case 'mislukt':
        return [t('qa.eigen.storingMislukt')];
      default:
        return [t('qa.eigen.werksoortNietBerekend')];
    }
  })();

  return (
    <section
      className="flex min-w-0 flex-col gap-2 rounded-[8px] border border-dashed border-[var(--border-strong)] bg-[var(--background-muted)] p-5"
      data-testid={`werkkwaliteit-ondergrens-${sleutel}`}
    >
      <Kop className={BLOK_KOP}>{naam}</Kop>
      {zinnen.map((zin, plek) => (
        <p className={`text-[0.875rem] ${plek === 0 ? 'font-medium text-[var(--foreground)]' : 'text-[var(--foreground-muted)]'}`} key={zin}>
          {zin}
        </p>
      ))}
    </section>
  );
}

export interface RapportkaartProps {
  week: EigenPersoonsweek;
  /** Het niveau van de koppen op het hoogste niveau van de kaart (story 11 gebruikt 3). */
  kopNiveau: 2 | 3 | 4;
}

/** Oudere meetmethode (M13): één melding, geen cijfers. Gedeeld met Details (story 11), zodat kader en Details dezelfde toets doen. */
export function isOudeMeetmethode(week: EigenPersoonsweek, standen: ReadonlyArray<{ stand: Stand }>): boolean {
  return typeof week.werksoorten !== 'object' || week.werksoorten === null || isVoorWeekrapport(week.afbakening) || standen.every(({ stand }) => stand.soort === 'oud');
}

/**
 * De weekweergave van Mijn feedback als rapportkaart (variant A, SPEC-werkkwaliteit-herontwerp story 9).
 *
 * Per werksoort een Resultaatblok (of een Onder-de-grens-blok), per werksoort met cijfer de drie
 * Aandachtskaarten, de rest ingeklapt in "Meer over deze week", en de Voettekst. Elk getal komt klaar uit het
 * antwoord (AD-19); hier wordt alleen gesorteerd op aantal en gekozen welke tekst bij een reden hoort.
 */
export function Rapportkaart({ week, kopNiveau }: RapportkaartProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const Kop = `h${kopNiveau}` as const;
  const werksoorten = typeof week.werksoorten === 'object' && week.werksoorten !== null ? week.werksoorten : null;
  const standen = WERKSOORTEN.map(({ sleutel, naam }) => ({ sleutel, naam: t(naam), stand: standVan(werksoorten?.[sleutel]) }));
  const drempel = effectieveDrempel(week.afbakening.drempelProductenPerPersoon);
  const doel = isTelling(week.afbakening.doelInEenKeerGoed) ? week.afbakening.doelInEenKeerGoed : undefined;
  const voet = <AccountabilityFooter afbakening={week.afbakening} kopNiveau={kopNiveau} weekrapport weekscherm />;

  // Oudere meetmethode (M13): één melding, geen cijfers.
  if (isOudeMeetmethode(week, standen)) {
    return (
      <p className="text-[0.8125rem] text-[var(--foreground)]" data-testid="werkkwaliteit-week-oude-meetmethode">
        {t('qa.eigen.weekOudeMeetmethode')}
      </p>
    );
  }

  const periode = (
    <p className="text-[0.8125rem] text-[var(--foreground-muted)]">
      {t('qa.eigen.periode', {
        week: weeknummer(week.periode.weekKey),
        van: formatDatum(week.periode.since, locale),
        tot: formatDatum(week.periode.until, locale),
      })}
    </p>
  );

  if (standen.every(({ stand }) => stand.soort === 'geenWerk' || stand.soort === 'nietNagekeken')) {
    return (
      <>
        {periode}
        <p className="text-[0.8125rem] font-semibold text-[var(--foreground)]" data-testid="werkkwaliteit-niets-beoordeeld">
          {t('qa.rapportkaart.nietsNagekeken')}
        </p>
        {voet}
      </>
    );
  }

  const onbekend = t('qa.eigen.veldnaamOnbekend');
  const metCijfer = standen.flatMap(({ sleutel, naam, stand }) =>
    stand.soort === 'cijfer' ? [{ sleutel, naam, rapport: stand.rapport, cijfer: stand.cijfer, ...veldenVan(stand.rapport, week.veldnamen, onbekend) }] : [],
  );
  const meerOver: MeerOverWerksoort[] = metCijfer.map(({ sleutel, naam, rapport, cijfer, overig }) => ({
    sleutel,
    naam,
    overig,
    aantalMetCorrectie: rapport.aantalMetCorrectie,
    aantalBeoordeeld: cijfer.aantalBeoordeeld,
  }));

  return (
    <>
      {periode}
      <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
        {standen.map(({ sleutel, naam, stand }) =>
          stand.soort === 'cijfer' ? (
            <Resultaatblok cijfer={stand.cijfer} doel={doel} key={sleutel} kopNiveau={kopNiveau} naam={naam} rapport={stand.rapport} sleutel={sleutel} />
          ) : (
            <OnderDeGrensBlok drempel={drempel} key={sleutel} kopNiveau={kopNiveau} naam={naam} sleutel={sleutel} stand={stand} />
          ),
        )}
      </div>

      {metCijfer.map(({ sleutel, naam, rapport, cijfer, kaarten }) =>
        kaarten.length > 0 ? (
          <section className="flex min-w-0 flex-col gap-3" data-testid={`werkkwaliteit-aandachtspunten-${sleutel}`} key={sleutel}>
            <Kop className={BLOK_KOP}>{t('qa.rapportkaart.aandachtspuntenKop', { count: kaarten.length, werksoort: naam })}</Kop>
            <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-3">
              {kaarten.map((veld, index) => (
                <Aandachtskaart
                  aantalBeoordeeld={cijfer.aantalBeoordeeld}
                  key={veld.veld}
                  kopNiveau={(kopNiveau + 1) as 3 | 4 | 5}
                  plek={index + 1}
                  soort={sleutel}
                  veld={veld}
                  veldnamen={week.veldnamen}
                />
              ))}
            </div>
          </section>
        ) : rapport.aantalMetCorrectie === 0 ? (
          <p className="text-[0.8125rem] text-[var(--foreground)]" key={sleutel}>
            {t('qa.rapportkaart.allesGoed', { werksoort: naam })}
          </p>
        ) : null,
      )}

      {metCijfer.length > 0 && <MeerOverDezeWeek kopNiveau={kopNiveau} werksoorten={meerOver} />}
      {voet}
    </>
  );
}
